<script lang="ts" setup>
import { computed, defineExpose, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import router from '@/router';
import { IonButton, IonContent, IonIcon, IonItem, IonList, IonPopover, IonSpinner } from '@ionic/vue';
import { chevronDownOutline, chevronBackOutline, chevronForwardOutline, warning } from 'ionicons/icons';
import { DateTime } from 'luxon';
import { captureMessage } from '@/sentry';
import { useI18n } from 'vue-i18n';

import { useAuthStore } from '@/stores/auth/authStore';
import { Wallet } from '../interfaces/balance';
import useTenant from '@/shared/composables/useTenant';
import useToast from '@/shared/composables/useToast';
import useUtils from '@/shared/composables/useUtils';
import useWallet from '@/modules/wallet/composables/useWallet';
import useStorageService from '@/shared/composables/useStorageService';
import useUserProfile from '@/modules/profile/composables/useUserProfile';
import useQuickTopUp from '@/modules/top-up/composables/useQuickTopUp';
import QuickTopUpCard from '@/shared/components/ui/QuickTopUpCard.vue';

const props = defineProps({
	user: { type: String, default: '' },
	walletId: { type: String, default: '' },
});

// Init
const { t } = useI18n();
const emit = defineEmits(['onWalletSet']);
const { presentToast } = useToast();
const authStore = useAuthStore();
const { getWallets, selectedWallet, getMinimumTopUpAmount } = useWallet();
const { getTenantConfigAndStyles } = useTenant();
const { tenantConfig, tenantStyles } = getTenantConfigAndStyles();
const { minimumTopUp } = getMinimumTopUpAmount(computed(() => selectedWallet.value?.id?.toString()));
	const { quickPackages, isLoadingQuickPackages, fetchQuickPackages, handleQuickTopUp } = useQuickTopUp(tenantConfig, minimumTopUp);
const { refetchWalletsData, wallets, walletUpdatedAt } = getWallets();
const { convertToCurrency, toRelativeTime } = useUtils();
const unsubscribe = ref();
const isOnline = ref(navigator.onLine);
const startTime = ref(new Date());
const showWarningMessage = ref(false);
const { get, set } = useStorageService();
const { getUserProfile } = useUserProfile();
const { userProfile } = getUserProfile();
const walletUpdatedAtRelativeTime = ref();
const walletNotSynched = ref(false);
const preferredPaymentMode = computed(() => {
	if (tenantConfig.value?.viva_wallet_click_to_pay_preferred_mode) {
		return 'click_to_pay';
	}
	if (tenantConfig.value?.viva_wallet_payconiq_preferred_mode) {
		return 'payconiq';
	}
	return null;
});
const isPreferredPaymentMode = computed(() => preferredPaymentMode.value !== null);
const isPayconiqPreferredMode = computed(() => preferredPaymentMode.value === 'payconiq');
const isClickToPayPreferredMode = computed(() => preferredPaymentMode.value === 'click_to_pay');
const activeWallets = computed(() => wallets.value?.filter((w) => +w.current_balance > 0) ?? []);
const topUpButtonLabel = computed(() => {
	if (!selectedWallet.value || !isPreferredPaymentMode.value) {
		return t('wallet.add');
	}
	if (isPayconiqPreferredMode.value) {
		return `${t('wallet.add')} ${selectedWallet.value.currency.name} ${t('wallet.pay_with_payconiq')}`;
	}
	return `${t('wallet.pay_with_click_to_pay')}`;
});

// Expose the method to the parent component
defineExpose({
	refetchWalletsData,
});

if (authStore.isAuthenticated()) {
	refetchWalletsData();
}

const handleWalletChange = async () => {
	if (authStore.isAuthenticated() && wallets.value) {
		if (wallets.value) {
			if (wallets?.value?.length && wallets?.value?.length > 0) {
				let walletToSelect: Wallet | undefined;

				if (props.walletId) {
					walletToSelect = wallets.value?.find((wallet) => {
						return wallet.id == +props.walletId;
					});
				}

				if (!walletToSelect && selectedWallet.value) {
					walletToSelect = wallets.value.find((wallet) => wallet.id === selectedWallet.value?.id);
				}

				if (!walletToSelect) {
					const cachedWallet = await get('selectedWallet');
					if (cachedWallet) {
						walletToSelect = wallets.value.find((w) => w.id === cachedWallet.id);
					}
				}

				if (!walletToSelect) {
					walletToSelect = wallets.value?.find((wallet) => {
						return wallet.currency.default;
					});
				}

				if (!walletToSelect) {
					walletToSelect = wallets.value[0];
				}

				selectedWallet.value = walletToSelect;
			}
			emit('onWalletSet', selectedWallet.value);
			if (selectedWallet.value?.id) {
				fetchQuickPackages(selectedWallet.value.id.toString());
			}
		}
	}
};

const calculateWalletNotSynched = async () => {
	const now = DateTime.now();
	const walletUpdatedAtDate = DateTime.fromMillis(walletUpdatedAt.value);
	const diffInMilliseconds = now.diff(walletUpdatedAtDate).milliseconds;

	if (diffInMilliseconds > 1000 * 60 * 1) {
		walletNotSynched.value = true;
	} else {
		walletNotSynched.value = false;
	}
};

const calculateRelativeTime = async () => {
	walletUpdatedAt.value = await get('walletUpdatedAt');
	if (walletUpdatedAt.value) {
		walletUpdatedAtRelativeTime.value = toRelativeTime(t, walletUpdatedAt.value);
		calculateWalletNotSynched();
	}
};

onMounted(async () => {
	await handleWalletChange();
	await calculateRelativeTime();
});

watch(wallets, async () => {
	if (wallets.value) {
		await handleWalletChange();
		await calculateRelativeTime();
	}
});

watch(walletUpdatedAt, async () => {
	await calculateRelativeTime();
});

setInterval(async () => {
	await calculateRelativeTime();
}, 15 * 1000);

const warningMessageInterval = setInterval(() => {
	const seconds = Math.round(DateTime.now().diff(DateTime.fromJSDate(startTime.value)).milliseconds / 1000);
	if (seconds > 5 && !selectedWallet.value) {
		showWarningMessage.value = true;
		clearInterval(warningMessageInterval);
	} else if (seconds > 5) {
		clearInterval(warningMessageInterval);
	} else {
		showWarningMessage.value = false;
	}
}, 1000);

onBeforeUnmount(() => {
	if (unsubscribe.value) {
		unsubscribe.value();
	}
});

const selectWallet = async (wallet: Wallet | undefined) => {
	if (!wallet) {
		captureMessage(`Attempted to select a wallet that does not exist`);
	}
	selectedWallet.value = wallet;
	await set('selectedWallet', wallet);
	emit('onWalletSet', wallet);
};

const getWalletAtIndex = (index: number) => {
	const availableWallets = wallets.value ?? [];

	if (!availableWallets.length) {
		return undefined;
	}

	if (index >= availableWallets.length) {
		index = 0;
	} else if (index < 0) {
		index = availableWallets.length - 1;
	}
	return availableWallets[index];
};

const currentWalletIndex = () => {
	if (!wallets.value || !selectedWallet.value) {
		return 0;
	}

	return wallets.value.findIndex((wallet) => wallet.id === selectedWallet.value?.id);
};

const getPreviousWallet = () => {
	return getWalletAtIndex(currentWalletIndex() - 1);
};

const getNextWallet = () => {
	return getWalletAtIndex(currentWalletIndex() + 1);
};

const selectPreviousWallet = () => {
	selectWallet(getPreviousWallet());
};

const selectNextWallet = () => {
	selectWallet(getNextWallet());
};

const hideOfflineMessage = () => {
	isOnline.value = true;
};

const showOfflineMessage = () => {
	isOnline.value = false;
};

window.addEventListener('offline', () => {
	showOfflineMessage();
});

window.addEventListener('online', () => {
	hideOfflineMessage();
});

const goTo = async (link: string) => {
	// If the user is not authenticated, we will first ask him to sign in
	if (!authStore.isAuthenticated()) {
		await presentToast('top', t('general.please_login_first'), 5000, 'danger');
		await router.push('/auth/signuphome');
	} else {
		if (selectedWallet.value?.currency) {
			await router.push(link);
		} else {
			await presentToast('top', t('error.offline'), 5000, 'danger');
		}
	}
};

const onQuickTopUp = (walletPackage) => {
	handleQuickTopUp(walletPackage);
};

const walletLayoutImage = ref(userProfile?.value?.wallet_layout_image ?? null);
const walletLayoutTextColor = ref(userProfile?.value?.wallet_layout_text_color ?? null);

watch(
	() => userProfile?.value,
	(newValue) => {
		walletLayoutImage.value = newValue?.wallet_layout_image ?? null;
		walletLayoutTextColor.value = newValue?.wallet_layout_text_color ?? null;
	},
	{ deep: true },
);
</script>

<template>
	<div
		v-if="!tenantConfig?.hide_wallet"
		:class="[
			{
				'mb-2': !isPreferredPaymentMode,
				'mb-8': isPreferredPaymentMode,
			},
		]">
		<div v-if="isOnline && authStore.isAuthenticated() && activeWallets.length > 0" class="flex gap-2 mb-2 overflow-x-auto wallet-badges-row mx-[34px]">
			<div
				v-for="wallet in activeWallets"
				:key="wallet.id"
				class="wallet-badge flex-shrink-0 cursor-pointer"
				:class="{ 'wallet-badge--active': selectedWallet?.id === wallet.id }"
				@click="selectWallet(wallet)">
				{{ wallet.currency.prefix }} {{ convertToCurrency(+wallet.current_balance) }}
			</div>
		</div>
		<div class="flex">
			<div v-if="isOnline && authStore.isAuthenticated()" class="flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer">
				<ion-icon v-if="wallets && wallets?.length > 1" :icon="chevronBackOutline" class="text-white active:bg-white/30 active:rounded-full active:p-1" @click="selectPreviousWallet()"></ion-icon>
			</div>
			<div
				:class="[
					{
						'bg-white-image': !isPreferredPaymentMode,
						'payconiq': isPayconiqPreferredMode,
						'click-to-pay-wallet': isClickToPayPreferredMode,
						'text-white': isPreferredPaymentMode,
						'font-color-dark': !isPreferredPaymentMode,
						'mx-[34px]': !isOnline || !authStore.isAuthenticated(),
					},
				]"
				class="flex flex-col justify-between rounded-md w-full"
				:style="{
					'background-image': !isPreferredPaymentMode && walletLayoutImage ? `url(${walletLayoutImage})` : undefined,
					'background-size': 'cover',
					'color': !isPreferredPaymentMode && walletLayoutImage && walletLayoutTextColor ? walletLayoutTextColor + ' !important' : undefined,
				}">
				<div
					class="flex flex-col justify-between w-full p-4"
					:class="[
						{
							'px-4': !authStore.isAuthenticated(),
						},
					]">
					<div v-if="isOnline && authStore.isAuthenticated() && !selectedWallet" class="flex w-full items-center justify-center">
						<ion-spinner
							name="dots"
							:class="[
								{
									'text-anykrowd-dark': !walletLayoutImage && !isPreferredPaymentMode,
								},
							]"
							:style="{
								'color': !isPreferredPaymentMode && walletLayoutImage && walletLayoutTextColor ? walletLayoutTextColor + ' !important' : undefined,
							}"></ion-spinner>
					</div>
					<div v-if="authStore.isAuthenticated() && showWarningMessage && !selectedWallet" class="text-base">
						{{ t('general.connection.offline_long_message') }}
					</div>

					<div v-if="authStore.isAuthenticated()" class="flex flex-row justify-between" :class="{ 'mb-6': !selectedWallet?.currency?.description }">
						<div v-if="selectedWallet" class="flex">
							<div class="flex flex-col">
								<div
									class="font-sourcesanspro-semibold text-sm"
									:class="[
										{
											'text-anykrowd-dark/50': !walletLayoutImage && !isPreferredPaymentMode,
										},
									]">
									{{ t('components.wallet_box.your_balance', { currency: selectedWallet.currency.name }) }}
								</div>
								<div class="flex flex-row items-center">
									<div v-if="wallets && wallets.length > 0 && (walletNotSynched || !isOnline)" class="flex items-center">
										<ion-icon id="popover-wallet-alert" size="medium" :icon="warning" class="mr-1" :style="`color: ${tenantStyles?.tertiary_color}`"></ion-icon>
										<ion-popover trigger="popover-wallet-alert" :dismiss-on-select="true">
											<ion-content class="ion-padding bg-black">
												{{ t('wallet.not_synched') }}
											</ion-content>
										</ion-popover>
									</div>
									<span class="font-sourcesanspro-black text-lg3">
										{{ selectedWallet.currency.prefix }}
										{{ convertToCurrency(+selectedWallet.current_balance) }}
									</span>
								</div>
							</div>

							<!-- <span class="font-sourcesanspro-regular text-lg"> + 2</span> -->
						</div>
						<div class="flex justify-between mb-2">
							<div></div>
							<div v-if="selectedWallet">
								<div
									id="popover-button"
									class="flex items-center justify-center rounded-lg font-sourcesanspro-bold text-md cursor-pointer"
									:class="[
										{
											'text-anykrowd-dark': !walletLayoutImage && !isPreferredPaymentMode,
										},
									]">
									<div class="mr-2">{{ selectedWallet.currency.prefix }}</div>
									<div class="mr-2">{{ selectedWallet?.currency.name }}</div>
									<ion-icon
										v-if="wallets && wallets?.length > 1"
										:icon="chevronDownOutline"
										class="text-md"
										:class="[
											{
												'text-anykrowd-dark': !walletLayoutImage && !isPreferredPaymentMode,
											},
										]"
										:style="{
											'color': !isPreferredPaymentMode && walletLayoutImage && walletLayoutTextColor ? walletLayoutTextColor + ' !important' : undefined,
										}"></ion-icon>
								</div>
								<ion-popover trigger="popover-button" :dismiss-on-select="true">
									<ion-content>
										<ion-list>
											<ion-item v-for="wallet in wallets" class="flex items-center justify-center ion-no-padding ion-no-margin" :key="wallet.id" :button="true" @click="selectWallet(wallet)" :detail="false">
												<div class="flex flex-col px-3 py-2 font-sourcesanspro-regular">
													<div class="mb-1">
														<span>
															{{ wallet.currency.prefix }}
															{{ wallet.current_balance }}
															{{ wallet.currency.name }}
														</span>
													</div>
													<!-- <span class="text-sm text-white/60">+2 BONUS COOKIES</span> -->
												</div>
											</ion-item>
										</ion-list>
									</ion-content>
								</ion-popover>
							</div>
						</div>
					</div>
					<div
						v-if="authStore.isAuthenticated() && selectedWallet && selectedWallet.currency?.description"
						class="flex flex-col font-sourcesanspro-semibold text-sm mb-6 mt-4 break-words"
						:class="[
							{
								'text-anykrowd-dark/50': !walletLayoutImage && !isPreferredPaymentMode,
							},
						]">
						{{ selectedWallet.currency?.description }}
					</div>

					<div class="flex items-center justify-between">
						<ion-button
							v-if="isOnline && authStore.isAuthenticated() && selectedWallet && selectedWallet.currency.topupable"
							:class="[
								{
									'black': !isPreferredPaymentMode,
									'preferred-wallet-button': isPreferredPaymentMode,
									'click-to-pay-wallet-button': isClickToPayPreferredMode,
								},
							]"
							:shape="!isPreferredPaymentMode ? 'round' : undefined"
							class="w-full h-[40px] font-sourcesanspro-semibold text-sm2 ion-no-margin"
							@click="goTo(`/top-up/${selectedWallet!.id}`)">
							<img v-if="isPayconiqPreferredMode" :src="require('@/statics/png/payconiq/payconiq-horizontal.png')" class="mr-1" />
							<span v-if="isClickToPayPreferredMode" class="mastercard-mark wallet-mastercard-mark" aria-hidden="true"><span></span><span></span></span>
							<span class="wallet-button-label">{{ topUpButtonLabel }}</span>
						</ion-button>
					</div>
					<div v-if="isOnline && authStore.isAuthenticated() && (quickPackages.length > 0 || isLoadingQuickPackages)" class="mt-3">
						<QuickTopUpCard :quick-packages="quickPackages" :loading="isLoadingQuickPackages" @on-quick-top-up="onQuickTopUp" />
					</div>
				</div>
			</div>
			<div v-if="isOnline && authStore.isAuthenticated()" class="flex w-[34px] min-w-[34px] items-center justify-center cursor-pointer">
				<ion-icon v-if="wallets && wallets?.length > 1" :icon="chevronForwardOutline" class="text-white active:bg-white/30 active:rounded-full active:p-1" @click="selectNextWallet()"></ion-icon>
			</div>
		</div>

		<div v-if="isOnline && walletUpdatedAtRelativeTime" class="flex px-[34px] mb-3 mt-[2px] justify-end font-sourcesanspro-regular text-xs">
			{{ t('wallet.updated_at') }}
			{{ walletUpdatedAtRelativeTime }}
		</div>

		<div v-if="isOnline && isPayconiqPreferredMode" class="flex px-[34px] mx-auto items-center justify-between w-full">
			<span class="font-sourcesanspro-semibold drop-shadow-custom text-xs">{{ t('components.powered_by_payconiq') }}</span>
			<img :src="require('@/statics/svg/payconiq/payconiq-horizontal-neg.svg')" class="w-[74px] h-[13px] drop-shadow-custom" />
		</div>
	</div>
</template>

<style lang="scss" scoped>
.bg-white-image {
	background: url('@/statics/png/wallet/wallet-bg.png') no-repeat;
	background-size: cover;
}

.payconiq {
	background: url('@/statics/png/payconiq/bg.png') no-repeat;
	background-size: cover;
}

.click-to-pay-wallet {
	background: linear-gradient(135deg, #e60024 0%, #f02d24 46%, #ff9e1b 100%);
	background-size: cover;
	box-shadow: inset 0 1px 0 rgb(255 255 255 / 18%);
}

.preferred-wallet-button {
	--background: #ffffff !important;
	--background-activated: #ffffff;
	--background-activated-opacity: 1;
	--background-focused: #ffffff;
	--background-focused-opacity: 1;
	--background-hover: #ffffff;
	--background-hover-opacity: 1;
	--border-radius: 999px;
	--box-shadow: none;
	--color: #121212;
	text-transform: none;
}

.preferred-wallet-button:hover,
.preferred-wallet-button:focus,
.preferred-wallet-button:focus-visible,
.preferred-wallet-button.ion-activated,
.preferred-wallet-button.ion-focused {
	--background: #ffffff !important;
	--background-hover: #ffffff;
	--background-hover-opacity: 1;
	--color: #121212;
}

.preferred-wallet-button::part(native),
.preferred-wallet-button:hover::part(native),
.preferred-wallet-button:focus::part(native),
.preferred-wallet-button:focus-visible::part(native) {
	background: #ffffff !important;
	color: #121212;
}

.click-to-pay-wallet-button {
	--color: #7a5634;
}

.click-to-pay-wallet-button:hover,
.click-to-pay-wallet-button:focus,
.click-to-pay-wallet-button:focus-visible,
.click-to-pay-wallet-button.ion-activated,
.click-to-pay-wallet-button.ion-focused {
	--color: #7a5634;
}

.click-to-pay-wallet-button::part(native),
.click-to-pay-wallet-button:hover::part(native),
.click-to-pay-wallet-button:focus::part(native),
.click-to-pay-wallet-button:focus-visible::part(native) {
	color: #7a5634;
}

.wallet-button-label {
	display: block;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.mastercard-mark {
	position: relative;
	display: inline-flex;
	align-items: center;
	width: 32px;
	height: 20px;
	flex: 0 0 32px;
}

.mastercard-mark span {
	position: absolute;
	top: 2px;
	width: 16px;
	height: 16px;
	border-radius: 999px;
}

.mastercard-mark span:first-child {
	left: 4px;
	background: #eb001b;
}

.mastercard-mark span:last-child {
	right: 4px;
	background: #f79e1b;
	mix-blend-mode: multiply;
}

.wallet-mastercard-mark {
	margin-right: 6px;
}

.powered-mastercard-mark {
	width: 28px;
	height: 18px;
	filter: drop-shadow(0 1px 2px rgb(0 0 0 / 28%));
}

.powered-mastercard-mark span {
	top: 2px;
	width: 14px;
	height: 14px;
}

ion-item {
	&::part(native) {
		padding-inline-start: 0 !important;
	}
}

.wallet-badges-row {
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
}

.wallet-badge {
	display: inline-flex;
	align-items: center;
	padding: 4px 10px;
	border-radius: 999px;
	font-family: 'sourcesanspro-regular', sans-serif;
	font-size: 12px;
	background: rgba(0, 0, 0, 0.2);
	color: rgba(255, 255, 255, 0.85);
	transition: all 0.2s ease;

	&--active {
		background: rgba(255, 255, 255, 0.25);
		color: #fff;
		font-family: 'sourcesanspro-bold', sans-serif;
	}

	&:active {
		transform: scale(0.95);
	}
}
</style>
